/**
 * BuriedVerse e04_shalom "Shalom" — FULL overlay composition.
 * Generated by bv_overlay_gen.py. fps-SAFE (S(sec) reads useVideoConfig().fps) so the SAME comp is:
 *   • buriedverse-e04-overlays      @16fps → the 720p 0.5x preview (baked logo)
 *   • buriedverse-e04-overlays-4k   @24fps → the Topaz 4K base, logo via ffmpeg (showLogo=false)
 * Devotional route (== bible_decoder W01): overlay_design_spec route=devotional; build_overlay_windows.py
 * EXTRACTS every `start: X, out: Y` NUMERIC LITERAL below → factory_4k_full renders those clusters @scale=3.
 * Anchors are REAL times from productions.word_timestamps_hindi. 29 events (9 BvFactTag + 20 BvHero).
 */
import {
  AbsoluteFill, Img, Sequence, staticFile, useCurrentFrame, useVideoConfig, interpolate,
} from "remotion";
import { BvFactTag, BvHero } from "./BuriedVerseOverlayDemo";

export const e04_shalom_FPS = 16;
export const e04_shalom_DURATION_FRAMES = 25931;
export const e04_shalom_FPS_4K = 24;
export const e04_shalom_DURATION_FRAMES_4K = 38897;

const BvLogo: React.FC = () => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 36], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <Img src={staticFile("buried_verse_diorama_ko.png")}
      style={{ position: "absolute", top: 20, right: 20, width: 70, height: "auto", opacity: fadeIn * 0.85 }} />
  );
};

const FadeOut: React.FC<{ dur: number; children: React.ReactNode }> = ({ dur, children }) => {
  const frame = useCurrentFrame();
  const o = interpolate(frame, [dur - 14, dur], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return <AbsoluteFill style={{ opacity: o }}>{children}</AbsoluteFill>;
};

type Tag = { start: number; out: number; kicker: string; value: string };
type Hero = {
  start: number; out: number; variant: "word" | "myth" | "quote"; kicker: string;
  word?: string; translit?: string; gloss?: string; headline?: string; attribution?: string;
};

// ── SLOT 1 · fact tags (BvFactTag: citation / place / author) ──
const TAGS: Tag[] = [
  // law about property. In the book of Exodus, when a man's animal destroys his ne
  { start: 126.757, out: 131.357, kicker: "Citation", value: "Exodus · shalem, make full restitution" },
  // Uriah, it says, about the shalom of Joab, and the shalom
  { start: 256.452, out: 261.052, kicker: "Citation", value: "2 Samuel 11 · David & Uriah" },
  // of everyone. And a priest named Phinehas, the grandson of
  { start: 442.534, out: 447.134, kicker: "Citation", value: "Numbers 25 · Phinehas" },
  // the scribes call a vav qetia. That break is
  { start: 523.279, out: 527.879, kicker: "Manuscript", value: "The broken vav (vav qetia) in “shalom”" },
  // for a slaughter. In Genesis thirty-four, after the men
  { start: 595.21, out: 599.81, kicker: "Citation", value: "Genesis 34 · Shechem, peace as the trap" },
  // the story of a woman named Jael. A defeated enemy
  { start: 641.406, out: 646.006, kicker: "Citation", value: "Judges 4 · Jael & Sisera, the truce" },
  // soften into sentiment. "The chastisement of our shalom was upon him." The word
  { start: 952.077, out: 956.677, kicker: "Citation", value: "Isaiah 53 · “the chastisement of our shalom”" },
  // times. King Solomon. In Hebrew his name is Shelomoh, and it is
  { start: 1012.336, out: 1016.936, kicker: "Author", value: "Solomon · Shelomoh, the man of peace" },
  // our three letters: the shelamim, usually translated the peace offering, the sh
  { start: 1263.774, out: 1268.374, kicker: "Citation", value: "Leviticus · the shelamim (peace offering)" },
];

// ── SLOT 2 · hero cards (BvHero: word / myth / quote) ──
const HEROES: Hero[] = [
  // him, in Hebrew, about the shalom of the war. The shalom of
  { start: 36.653, out: 43.653, variant: "myth", kicker: "The hook", headline: "You can ask after the shalom of a war. So it was never the opposite of conflict." },
  // in quiet rooms. It is forged. It is paid for. The first time
  { start: 78.676, out: 85.676, variant: "myth", kicker: "The thesis", headline: "In the Bible, shalom is not lying around in quiet rooms. It is forged. It is paid for." },
  // has, moments before, driven a spear clean through two people. So no. Shalom
  { start: 87.05, out: 94.05, variant: "myth", kicker: "The reveal", headline: "God’s first covenant of shalom went to a man who had just driven a spear through two people." },
  // three Hebrew letters, shin, lamed, mem, the root sh-l-m.
  { start: 110.593, out: 117.793, variant: "word", kicker: "The word", word: "שׁ־ל־ם", translit: "sh-l-m", gloss: "To be complete, whole, finished. Nothing missing, nothing owed." },
  // is squared. And a war has accounts. A war has
  { start: 171.886, out: 178.886, variant: "myth", kicker: "The second face", headline: "A war has accounts. Shalom asks how a thing stands, whether the matter is squared." },
  // is shouting, and there is not one whole thing in his life. Because shalom was
  { start: 303.325, out: 310.325, variant: "myth", kicker: "The irony", headline: "David’s rooms were quiet, his palace calm, and not one whole thing was in his life." },
  // king who asked. The word for peace opens the scene and a murder closes it, and
  { start: 380.408, out: 387.408, variant: "myth", kicker: "The gap", headline: "The word for peace opens the scene. A murder closes it, only a few verses apart." },
  // says of Phinehas, "I give to him my covenant of shalom." My covenant of
  { start: 468.83, out: 476.03, variant: "quote", kicker: "The covenant", headline: "“I give to him my covenant of shalom.” Handed to a man who just killed two with a spear.", attribution: "Numbers 25:12" },
  // is only this: the scribes left a visible flaw in the word for peace at precise
  { start: 568.696, out: 575.696, variant: "myth", kicker: "The crack", headline: "The scribes left a visible flaw in the word for peace, at the one place peace was forged with a weapon." },
  // for peace is the last thing the victim hears before the end. Now, I am
  { start: 671.219, out: 678.219, variant: "myth", kicker: "The weapon", headline: "In both stories the word for peace is the last thing the victim hears before the end." },
  // very same grammar: "I make shalom, and I create calamity." Peace and disaster,
  { start: 764.224, out: 771.424, variant: "quote", kicker: "The pairing", headline: "“I make shalom, and I create calamity.” Peace and disaster from the same hand.", attribution: "Isaiah 45:7" },
  // same hand. Now, the word translated calamity there is ra, and it does
  { start: 773.214, out: 780.414, variant: "word", kicker: "The word", word: "רַע", translit: "ra", gloss: "Catastrophe, ruin. Not moral evil, but the disaster that levels a city." },
  // soft reading misses. Sar is not a soft word. A sar is
  { start: 859.599, out: 866.799, variant: "word", kicker: "The word", word: "שַׂר שָׁלוֹם", translit: "Sar Shalom", gloss: "Not a gentle prince. A commander who forces the world’s accounts back into order." },
  // shalom only expands, like a front line moving forward, because it is
  { start: 916.698, out: 923.698, variant: "myth", kicker: "The advance", headline: "This shalom takes ground, like a front line moving forward. A peace that conquers." },
  // On this reading our shalom was purchased, and the bill
  { start: 974.275, out: 981.275, variant: "myth", kicker: "The cost", headline: "Our shalom was purchased, and the bill went to someone else." },
  // people lightly, saying, peace, peace, when there is no peace." Shalom, shalom,
  { start: 1124.044, out: 1131.244, variant: "quote", kicker: "The counterfeit", headline: "“Peace, peace, when there is no peace.” The account declared settled when nothing was paid.", attribution: "Jeremiah 6:14" },
  // will tell you the origin of the name is genuinely debated. The older evidence
  { start: 1204.211, out: 1211.211, variant: "myth", kicker: "The honest bit", headline: "“City of peace”? Jerusalem’s origin is debated, likely “foundation,” maybe a deity named Shalem." },
  // meal of peace. You did not get the table without the altar. The shared dinner
  { start: 1321.87, out: 1328.87, variant: "myth", kicker: "The order", headline: "The peace offering was the one you could eat. But the animal was slain first. No table without the altar." },
  // after the fight, the wholeness forged through it. Not the account
  { start: 1393.149, out: 1400.149, variant: "myth", kicker: "The forge", headline: "Shalom is not the silence after the fight. It is the wholeness forged through it." },
  // all that quiet, has the thing actually been settled, or has it only been silen
  { start: 1610.045, out: 1617.045, variant: "myth", kicker: "The question", headline: "Underneath the quiet, has the thing been settled, or only silenced?" },
];

export const BuriedVerseE04ShalomOverlays: React.FC<{ showLogo?: boolean }> = ({ showLogo = true }) => {
  const { fps } = useVideoConfig();
  const S = (sec: number) => Math.round(sec * fps);
  return (
    <AbsoluteFill>
      {showLogo && <BvLogo />}
      {TAGS.map((t, i) => (
        <Sequence key={`tag-${i}`} from={S(t.start)} durationInFrames={S(t.out - t.start)}>
          <FadeOut dur={S(t.out - t.start)}>
            <BvFactTag kicker={t.kicker} value={t.value} />
          </FadeOut>
        </Sequence>
      ))}
      {HEROES.map((h, i) => (
        <Sequence key={`hero-${i}`} from={S(h.start)} durationInFrames={S(h.out - h.start)}>
          <FadeOut dur={S(h.out - h.start)}>
            <BvHero variant={h.variant} kicker={h.kicker} word={h.word} translit={h.translit}
              gloss={h.gloss} headline={h.headline} attribution={h.attribution} />
          </FadeOut>
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
